import React, { Fragment, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { faArrowLeft, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Layouts } from 'app-components';
import GIButton from '@components/common/button/GIButton';
import { configs } from '@constants/configs/configs';
import withModalWrapper from '@containers/common/WithModalWrapper';
import { useModal } from '@hooks/useModal';

import AboutYouSection from './Sections/AboutYouSection';
import ApplicantInformationSection from './Sections/ApplicantSection';
import IdentificationSection from './Sections/IdentificationSection';
import MailingAddressSection from './Sections/MailingAddressSection';
import PoliticalPartySection from './Sections/PoliticalPartySection';
import RegistrationReasonSection from './Sections/RegistrationReasonSection';
import ResidentialAddressSection from './Sections/ResidentialAddressSection';
import VotingAssistanceSection from './Sections/VotingAssistanceSection';
import VotingBallotTransferSection from './Sections/VotingBallotTransferSection';
import VotingChangedInformationSection from './Sections/VotingChangedInformationSection';
import VotingDeclarationSection from './Sections/VotingDeclarationSection';
import VotingEligibilitySection from './Sections/VotingEligibilitySection';
import VotingFormHelpSection from './Sections/VotingFormHelpSection';
import VotingPollWorkerSection from './Sections/VotingPollWorkerSection';

import { SECTION_ORDER } from './constants/voterRegistrationConstants';
import { StyledVoterRegistrationFooter, VoterRegistrationWrapper } from './voterRegistration.styled';
import { IVoterRegistrationFormValues } from './voterRegistrationTypes';
import { voterRegistratationDefaultValues, voterRegistrationFormSchema } from './voterRegistrationFormSchema';
import useVoterRegistration from './useVoterRegistration';
import { modalConfig } from './models/modalConfig';
import { getMappedInitData, mapFormDataToApiFormat } from './utils/voterRegistrationUtils';

function VoterRegistrationContainer() {
    const { fetchInitData, saveVoterRegistration, voterRegistrationFormPath } = useVoterRegistration();
    const navigate = useNavigate();
    const [file, setFile] = useState<File | null>(null);
    const { openModal } = useModal();
    const { state } = useLocation();
    const { ssapApplicationId, caseNumber } = state || {};

    const formMethods = useForm<IVoterRegistrationFormValues>({
        defaultValues: voterRegistratationDefaultValues,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        resolver: zodResolver(voterRegistrationFormSchema),
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        getInitData();
    }, []);

    const { handleSubmit, setValue } = formMethods;

    const VOTER_REG_SECTIONS = () => [
        { Component: ApplicantInformationSection },
        { Component: VotingEligibilitySection },
        { Component: RegistrationReasonSection },
        { Component: AboutYouSection },
        { Component: ResidentialAddressSection },
        { Component: MailingAddressSection },
        { Component: IdentificationSection },
        { Component: PoliticalPartySection },
        { Component: VotingAssistanceSection },
        { Component: VotingChangedInformationSection },
        { Component: VotingFormHelpSection },
        { Component: VotingPollWorkerSection },
        { Component: VotingBallotTransferSection },
        { Component: VotingDeclarationSection, props: { setFile } },
    ];

    const getInitData = async () => {
        if (!ssapApplicationId) {
            console.error('SSAP Application ID is not provided');
            navigate(configs.routes.ELIGIBILITY_RESULTS);
            return;
        }

        const initData = await fetchInitData(ssapApplicationId);
        const mapped = getMappedInitData(initData);

        Object.entries(mapped).forEach(([field, value]) =>
            setValue(field as keyof IVoterRegistrationFormValues, value ?? ''),
        );
    };

    const onSubmit = (voterRegistrationFormData: IVoterRegistrationFormValues) => {
        const updatedVoterRequestStr = mapFormDataToApiFormat(voterRegistrationFormData);

        const requestFormData = new FormData();
        requestFormData.append('voterRequestStr', JSON.stringify(updatedVoterRequestStr));
        if (file instanceof File) {
            requestFormData.append('file', file);
        }

        saveVoterRegistration(requestFormData, caseNumber);
    };

    const onError = errors => {
        const sectionKey = SECTION_ORDER.find(key => errors[key]);
        if (sectionKey) {
            document.getElementById(`${sectionKey}-section`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const onBackClickHandler = () => {
        openModal('VoterRegistrationCancelModal');
    };

    return (
        <VoterRegistrationWrapper id="main-content" className="main-section__content">
            <Layouts id="voterRegistration">
                <div className="grid-container fade-in main-section__content--title">
                    <div className="subsection">
                        <div className="grid-row">
                            <GIButton
                                data-automation-id={'btn-back'}
                                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                                iconPlacement="start"
                                id={'btn-back'}
                                isDisabled={false}
                                className="margin-bottom-2"
                                labelId="voterRegistration.form.button.back"
                                name={'back'}
                                onClickHandler={onBackClickHandler}
                                type="button"
                                variant="secondary"
                                ariaLabel="Back to dashboard"
                            />
                        </div>
                        <h1 className="mobile:grid-col-12 tablet:grid-col-fill">
                            <FormattedMessage id="voterRegistration.form.header" />
                        </h1>
                        <ul className="margin-left-5">
                            <FormattedMessage
                                id="voterRegistration.form.header.list.1"
                                tagName="li"
                                values={{ strong: (chunks: any) => <strong>{chunks}</strong> }}
                            />
                            <FormattedMessage id="voterRegistration.form.header.list.2" tagName="li" />
                            <FormattedMessage
                                id="voterRegistration.form.header.list.3"
                                tagName="li"
                                values={{
                                    anchor: function makeAnchorTag(chunks: any) {
                                        return (
                                            <strong>
                                                <a href={voterRegistrationFormPath} download>
                                                    {chunks}
                                                </a>
                                            </strong>
                                        );
                                    },
                                }}
                            />
                        </ul>
                        <FormattedMessage id="voterRegistration.form.header.info" tagName="p" />

                        <hr />
                        <FormProvider {...formMethods}>
                            <form className="form-group" onSubmit={handleSubmit(onSubmit, onError)}>
                                {VOTER_REG_SECTIONS().map(({ Component, props = {} }, i) => (
                                    <Fragment key={Component.name}>
                                        <Component {...props} />
                                        {i < VOTER_REG_SECTIONS().length - 1 && <hr />}
                                    </Fragment>
                                ))}
                                <StyledVoterRegistrationFooter>
                                    <GIButton
                                        data-automation-id={'btn-back-to-top'}
                                        icon={<FontAwesomeIcon icon={faArrowUp} />}
                                        iconPlacement="start"
                                        id={'btn-back-to-top'}
                                        isDisabled={false}
                                        labelId="voterRegistration.form.button.backToTop"
                                        name={'back-to-top'}
                                        onClickHandler={() => {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        type="button"
                                        variant="secondary"
                                    />
                                    <GIButton
                                        id="submit-button"
                                        labelId="voterRegistration.form.button.submit"
                                        variant="primary"
                                        type="submit"
                                        ariaLabel="submit voter registration form"
                                    />
                                </StyledVoterRegistrationFooter>
                            </form>
                        </FormProvider>
                    </div>
                </div>
            </Layouts>
        </VoterRegistrationWrapper>
    );
}

export default withModalWrapper(VoterRegistrationContainer, modalConfig);
